/** Operational copy for Analysis intro by direction. See docs/PLATFORM_UI_PATTERNS.md § Analysis shared UI. */
export type SmartAnalysisDirection = 'candidates' | 'opportunities'

/** Shell-only: retry shows prior summary/progress above intro; copy is unchanged from idle. */
export type SmartAnalysisIntroMode = 'idle' | 'retry'

export type SmartAnalysisIntroCopy = {
  title: string
  description: string
  preferencesLabel: string
  preferencesPlaceholder: string
}

const ROLE_FALLBACK = 'this role'

export function getSmartAnalysisIntroCopy({
  direction,
  jobTitle,
  jobScoped,
  emptyActiveApplicants,
}: {
  direction: SmartAnalysisDirection
  /** Accepted for call-site parity; intro copy does not vary by mode. */
  mode?: SmartAnalysisIntroMode
  jobTitle?: string
  /** Employer job workspace Analysis tab (applicant cohort only). */
  jobScoped?: boolean
  /** No active applicants/invites on the job yet. */
  emptyActiveApplicants?: boolean
}): SmartAnalysisIntroCopy {
  const roleLabel = jobTitle?.trim() || ROLE_FALLBACK

  if (direction === 'opportunities') {
    return {
      title: 'Analysis',
      description:
        'Ranks published jobs against your profile with scores and reasoning. The Jobs tab is a plain directory; this is ranked search.',
      preferencesLabel: 'Additional preferences (optional)',
      preferencesPlaceholder: 'e.g. Prefer remote sustainability roles in Australia',
    }
  }

  if (jobScoped) {
    if (emptyActiveApplicants) {
      return {
        title: 'Compare your pipeline to this role',
        description:
          'Use Invite to add people, or wait for applications before running analysis.',
        preferencesLabel: 'Analysis criteria (optional)',
        preferencesPlaceholder:
          'e.g. Must have 5+ years in platform engineering; prefer hybrid in Melbourne',
      }
    }
    return {
      title: 'Compare your pipeline to this role',
      description: `Rank applicants and invited candidates for ${roleLabel} with scores and reasoning.`,
      preferencesLabel: 'Analysis criteria (optional)',
      preferencesPlaceholder:
        'e.g. Must have 5+ years in platform engineering; prefer hybrid in Melbourne',
    }
  }

  return {
    title: 'Analysis for this role',
    description: `Ranks candidates in your talent pool for ${roleLabel} with scores and reasoning.`,
    preferencesLabel: 'Additional preferences (optional)',
    preferencesPlaceholder:
      'e.g. Must have 5+ years in platform engineering; prefer hybrid in Melbourne',
  }
}
